import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, Loader2, ArrowLeft, CheckCircle } from "lucide-react";
import { isValidName, isValidEmail } from "@/lib/validation-utils";

const AfiliadoCadastro = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState<"form" | "success">("form");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", bio: "",
    pix_key: "", pix_type: "cpf",
  });

  const generateCode = (name: string) => {
    const base = name.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
    return base + Math.random().toString(36).substring(2, 6).toUpperCase();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast({ title: "Preencha nome, email e senha", variant: "destructive" });
      return;
    }
    const nameCheck = isValidName(form.name);
    if (!nameCheck.valid) {
      toast({ title: "Nome inválido", description: nameCheck.reason, variant: "destructive" });
      return;
    }
    const emailCheck = isValidEmail(form.email);
    if (!emailCheck.valid) {
      toast({ title: "E-mail inválido", description: emailCheck.reason, variant: "destructive" });
      return;
    }
    if (form.password.length < 6) {
      toast({ title: "A senha deve ter no mínimo 6 caracteres", variant: "destructive" });
      return;
    }

    setLoading(true);

    let userId: string;

    // Try to sign in first (user may already exist)
    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email: form.email,
      password: form.password,
    });

    if (signInData?.user) {
      userId = signInData.user.id;
      
      // Check if already an affiliate
      const { data: existingAffiliate } = await supabase
        .from("affiliates")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();
      
      if (existingAffiliate) {
        toast({ title: "Você já possui uma conta de afiliado", description: "Faça login no portal do afiliado.", variant: "destructive" });
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }
    } else {
      // Create new auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: { emailRedirectTo: window.location.origin + "/afiliado/login" },
      });

      if (authError) {
        toast({ title: "Erro no cadastro", description: authError.message, variant: "destructive" });
        setLoading(false);
        return;
      }

      if (!authData.user) {
        toast({ title: "Erro ao criar conta", variant: "destructive" });
        setLoading(false);
        return;
      }
      
      userId = authData.user.id;
    }

    const { error: affError } = await supabase.from("affiliates").insert({
      user_id: userId,
      name: form.name,
      email: form.email,
      code: generateCode(form.name),
      commission_type: "recurring",
      commission_percentage: 0,
      status: "pending",
      pix_key: form.pix_key || null,
      pix_type: form.pix_type || null,
      bio: form.bio || null,
    });

    if (affError) {
      toast({ title: "Erro ao criar perfil de afiliado", description: affError.message, variant: "destructive" });
      setLoading(false);
      return;
    }

    await supabase.auth.signOut();
    setLoading(false);
    setStep("success");
  };

  if (step === "success") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center space-y-4">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
          <h1 className="text-2xl font-bold font-display">Cadastro Enviado!</h1>
          <p className="text-muted-foreground">
            Sua solicitação foi enviada para análise. Você receberá um email quando sua conta for aprovada.
            Verifique também seu email para confirmar o cadastro.
          </p>
          <Button onClick={() => navigate("/afiliado/login")} variant="outline" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Ir para Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <UserPlus className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold font-display">Seja um Afiliado</h1>
          <p className="text-sm text-muted-foreground mt-1">Ganhe comissões indicando restaurantes para a plataforma</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2 sm:col-span-1">
              <Label className="text-sm">Nome Completo *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-10" />
            </div>
            <div className="space-y-1.5 col-span-2 sm:col-span-1">
              <Label className="text-sm">Telefone</Label>
              <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(11) 99999-9999" className="h-10" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Email *</Label>
            <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-10" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Senha *</Label>
            <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Mínimo 6 caracteres" className="h-10" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Sobre Você</Label>
            <Textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} placeholder="Como pretende divulgar a plataforma?" rows={3} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">Tipo PIX</Label>
              <select
                value={form.pix_type}
                onChange={e => setForm(f => ({ ...f, pix_type: e.target.value }))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
                <option value="email">Email</option>
                <option value="phone">Telefone</option>
                <option value="random">Aleatória</option>
              </select>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-sm">Chave PIX</Label>
              <Input value={form.pix_key} onChange={e => setForm(f => ({ ...f, pix_key: e.target.value }))} placeholder="Sua chave PIX para recebimento" className="h-10" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full h-11 bg-gradient-primary text-primary-foreground">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Enviar Cadastro
          </Button>
        </form>

        <div className="text-center mt-4">
          <p className="text-sm text-muted-foreground">
            Já é afiliado?{" "}
            <Link to="/afiliado/login" className="text-primary font-medium hover:underline">Fazer Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AfiliadoCadastro;
